"use client";

import { useT } from "@/lib/i18n";
import { useRouter } from "@/i18n/navigation";
import { Link } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import EntityPicker, { type PickerItem } from "@/app/components/EntityPicker";
import CharacterTag from "@/app/components/CharacterTag";
import { characterHex } from "@/lib/character-colors";
import LabUnavailable, { type LabUnavailableKind, unavailableKind } from "@/app/components/LabUnavailable";
import { DEFAULT_LIMIT, LIMIT_STEPS, LIST_KEYS, MAX_COPIES, MAX_LIMIT, SEED_FINDER_CHARACTERS, hasPredicates, paramsFromState, formatPick, pick, predicateCount, stateFromParams, type ListKey, type Pick, type SeedFinderState } from "@/lib/seed-finder-state";
const API = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
const HISTORY_KEY = "spire-codex:seed-finder-history";
const HISTORY_MAX = 8;
interface FinderRow {
  predicted?: boolean;
  seed: string;
  build_id: string | null;
  players: number;
  party: string[];
  runs: number;
  wins: number;
  win_rate: number | null;
  last_played: string | null;
  neow_offers: string[];
  bosses: {
    act: number;
    id: string;
  }[];
  ancients: {
    act: number;
    id: string;
  }[];
  best_run: {
    run_hash: string;
    url: string;
    win: boolean;
    run_time: number | null;
    floors: number | null;
    character: string | null;
  } | null;
  replay: {
    run_hash: string;
    url: string;
  } | null;
  matched: string[];
  missing: string[];
  full_match: boolean;
  where: Record<string, {
    act: number | null;
    floor: number | null;
  }[]>;
}
interface FinderResponse {
  available: boolean;
  detail?: string;
  engine?: "index" | "legacy";
  index?: {
    seeds?: number;
    builds?: string[];
    built_at?: string;
  };
  results?: FinderRow[];
  labels?: string[];
}
interface Catalogs {
  cards: PickerItem[];
  relics: PickerItem[];
  ancientRelics: PickerItem[];
  potions: PickerItem[];
  events: PickerItem[];
  ancients: PickerItem[];
  bosses: PickerItem[];
  elites: PickerItem[];
}
const EMPTY_CATALOGS: Catalogs = {
  cards: [],
  relics: [],
  ancientRelics: [],
  potions: [],
  events: [],
  ancients: [],
  bosses: [],
  elites: []
};
function characterLabel(c: string): string {
  return c.charAt(0) + c.slice(1).toLowerCase();
}
function isDraftable(c: {
  rarity_key?: string;
  type_key?: string;
}): boolean {
  const r = (c.rarity_key || "").toLowerCase();
  return r !== "starter" && r !== "basic" && r !== "none";
}
function readHistory(): string[] {
  try {
    const raw = window.localStorage.getItem(HISTORY_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(x => typeof x === "string").slice(0, HISTORY_MAX) : [];
  } catch {
    return [];
  }
}
function writeHistory(list: string[]) {
  try {
    window.localStorage.setItem(HISTORY_KEY, JSON.stringify(list));
  } catch {
    /* per-viewer convenience only */
  }
}
function formatTime(seconds: number | null | undefined): string {
  if (!seconds) return "";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}
export default function SeedFinderClient() {
  const t = useT();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [state, setState] = useState<SeedFinderState>(() => stateFromParams(new URLSearchParams(searchParams.toString())));
  const [catalogs, setCatalogs] = useState<Catalogs>(EMPTY_CATALOGS);
  const [allCardNames, setAllCardNames] = useState<Record<string, string>>({});
  const [catalogError, setCatalogError] = useState(false);
  const [catalogTick, setCatalogTick] = useState(0);
  const [versions, setVersions] = useState<string[]>([]);
  const [indexInfo, setIndexInfo] = useState<{
    seeds?: number;
    builds?: string[];
  } | null>(null);
  const [result, setResult] = useState<FinderResponse | null>(null);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<LabUnavailableKind | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [history, setHistory] = useState<string[]>([]);
  const autoRan = useRef(false);
  const written = useRef<string | null>(null);
  const seq = useRef(0);
  const inflight = useRef<AbortController | null>(null);
  useEffect(() => {
    const qs = paramsFromState(state).toString();
    written.current = qs;
    const current = searchParams.toString();
    if (qs !== current) router.replace(`/seed-finder${qs ? `?${qs}` : ""}`, {
      scroll: false
    });
  }, [state, router, searchParams]);
  useEffect(() => {
    const current_0 = searchParams.toString();
    if (written.current === null || current_0 === written.current) return;
    seq.current += 1;
    inflight.current?.abort();
    setState(stateFromParams(new URLSearchParams(current_0)));
    setResult(null);
    setProblem(null);
    setLimit(DEFAULT_LIMIT);
    autoRan.current = false;
  }, [searchParams]);
  useEffect(() => {
    setHistory(readHistory());
    fetch(`${API}/api/runs/seed-finder/meta`).then(r => r.ok ? r.json() : null).then(m => {
      if (m && m.available) setIndexInfo(m);
    }).catch(() => {});
    fetch(`${API}/api/runs/versions`).then(r_0 => r_0.ok ? r_0.json() : null).then(v => {
      if (v && Array.isArray(v.versions)) setVersions(v.versions);
    }).catch(() => {});
  }, []);
  const characterFilter = state.characters.length === 1 ? state.characters[0] : null;
  useEffect(() => {
    let dead = false;
    async function get(path: string) {
      const r_1 = await fetch(`${API}${path}`);
      if (!r_1.ok) throw new Error(String(r_1.status));
      return r_1.json();
    }
    async function load() {
      setCatalogError(false);
      try {
        const cardUrls = characterFilter ? [`/api/cards?color=${characterFilter.toLowerCase()}`, `/api/cards?color=colorless`] : ["/api/cards"];
        const [cardLists, relics, potions, events, encounters] = await Promise.all([Promise.all(cardUrls.map(get)), get("/api/relics"), get("/api/potions"), get("/api/events"), get("/api/encounters")]);
        if (dead) return;
        const item = (x: {
          id: string;
          name: string;
        }) => ({
          id: String(x.id).toUpperCase(),
          name: x.name
        });
        setAllCardNames(Object.fromEntries(cardLists.flat().map((c: {
          id: string;
          name: string;
        }) => [String(c.id).toUpperCase(), c.name])));
        setCatalogs({
          cards: cardLists.flat().filter(isDraftable).map(item),
          relics: relics.map(item),
          ancientRelics: relics.filter((r_2: {
            rarity_key?: string;
          }) => r_2.rarity_key === "Ancient").map(item),
          potions: potions.map(item),
          events: events.filter((e: {
            type?: string;
          }) => e.type !== "Ancient").map(item),
          ancients: events.filter((e_0: {
            type?: string;
          }) => e_0.type === "Ancient").map(item),
          bosses: encounters.filter((e_1: {
            room_type?: string;
          }) => e_1.room_type === "Boss").map(item),
          elites: encounters.filter((e_2: {
            room_type?: string;
          }) => e_2.room_type === "Elite").map(item)
        });
      } catch {
        if (!dead) setCatalogError(true);
      }
    }
    load();
    return () => {
      dead = true;
    };
  }, [characterFilter, catalogTick]);
  const names = useMemo(() => {
    const m_0: Record<string, string> = {
      ...allCardNames
    };
    for (const list of Object.values(catalogs)) for (const i of list) m_0[i.id] = i.name;
    return m_0;
  }, [catalogs, allCardNames]);
  const nameOf = useCallback((id: string) => names[id] || id.replace(/_/g, " ").toLowerCase(), [names]);
  const search = useCallback(async (wanted: number) => {
    if (!hasPredicates(state)) return;
    const mine = ++seq.current;
    inflight.current?.abort();
    const controller = new AbortController();
    inflight.current = controller;
    setLoading(true);
    setResult(null);
    setProblem(null);
    try {
      const params = paramsFromState(state);
      params.set("limit", String(wanted));
      const res = await fetch(`${API}/api/runs/seed-finder?${params.toString()}`, {
        signal: controller.signal
      });
      if (mine !== seq.current) return;
      if (res.status === 429) {
        setProblem("rate_limited");
        return;
      }
      if (res.status === 503) {
        setProblem("index_building");
        return;
      }
      if (!res.ok) {
        setProblem("error");
        return;
      }
      const data = (await res.json()) as FinderResponse;
      if (mine !== seq.current) return;
      if (!data.available) {
        setProblem(unavailableKind(data.detail));
        return;
      }
      setResult(data);
      const qs_0 = params.toString();
      const next = [qs_0, ...readHistory().filter(h => h !== qs_0)].slice(0, HISTORY_MAX);
      writeHistory(next);
      setHistory(next);
    } catch {
      if (mine === seq.current) setProblem("network");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [state]);
  useEffect(() => {
    if (autoRan.current) return;
    autoRan.current = true;
    if (hasPredicates(state)) search(DEFAULT_LIMIT);
  }, [state, search]);
  function update(patch: Partial<SeedFinderState>) {
    seq.current += 1;
    inflight.current?.abort();
    setState(s => ({
      ...s,
      ...patch
    }));
    setResult(null);
    setProblem(null);
    setLimit(DEFAULT_LIMIT);
  }
  function addPick(key: ListKey, i_0: PickerItem, extra: Partial<Pick> = {}) {
    const next_0 = pick(i_0.id, extra);
    update({
      [key]: state[key].some(p => formatPick(p) === formatPick(next_0)) ? state[key] : [...state[key], next_0]
    });
  }
  function patchPick(key_0: ListKey, index: number, patch_0: Partial<Pick>) {
    update({
      [key_0]: state[key_0].map((p_0, i_1) => i_1 === index ? {
        ...p_0,
        ...patch_0
      } : p_0)
    });
  }
  function dropPick(key_1: ListKey, index_0: number) {
    update({
      [key_1]: state[key_1].filter((_, i_2) => i_2 !== index_0)
    });
  }
  function toggleCharacter(c_0: string) {
    const has = state.characters.includes(c_0);
    const max = state.players ?? 1;
    let next_1 = has ? state.characters.filter(x_0 => x_0 !== c_0) : [...state.characters, c_0];
    if (!has && next_1.length > max) next_1 = next_1.slice(next_1.length - max);
    update({
      characters: next_1
    });
  }
  function labelFor(tag: string): string {
    const kind = tag.slice(0, tag.indexOf(":"));
    const rest = tag.slice(tag.indexOf(":") + 1);
    const [id_0, ...mods] = rest.split(" ");
    const parts: string[] = [];
    for (const m_1 of mods) {
      if (m_1.startsWith("x")) parts.push(`×${m_1.slice(1)}`);else if (m_1.startsWith("act")) parts.push(t("act {n}", {
        n: m_1.slice(3)
      }));else if (m_1.startsWith("<=f")) parts.push(t("by floor {n}", {
        n: m_1.slice(3)
      }));else if (m_1.startsWith(">=f")) parts.push(t("from floor {n}", {
        n: m_1.slice(3)
      }));else if (m_1.startsWith("seat")) parts.push(t("seat {n}", {
        n: m_1.slice(4)
      }));
    }
    const name = [nameOf(id_0), ...parts].join(" · ");
    const verbs: Record<string, string> = {
      deck: t("kept {name}", {
        name
      }),
      offered: t("offered {name}", {
        name
      }),
      relic: t("got {name}", {
        name
      }),
      event: t("saw {name}", {
        name
      }),
      neow: t("Neow offered {name}", {
        name
      }),
      ancient_offer: t("ancient offered {name}", {
        name
      }),
      ancient: t("met {name}", {
        name
      }),
      boss: t("boss {name}", {
        name
      }),
      elite: t("elite {name}", {
        name
      }),
      shop_card: t("shop sold {name}", {
        name
      }),
      shop_relic: t("shop sold {name}", {
        name
      }),
      shop_potion: t("shop sold {name}", {
        name
      })
    };
    return verbs[kind] ?? name;
  }
  function copy(text: string, key_2: string) {
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(key_2);
      setTimeout(() => setCopied(c_1 => c_1 === key_2 ? null : c_1), 1500);
    });
  }
  function copyLink() {
    const url = new URL(window.location.href);
    url.search = paramsFromState(state).toString();
    copy(url.toString(), "__link__");
  }
  async function randomSeed() {
    try {
      const params_0 = new URLSearchParams();
      if (state.buildId) params_0.set("build_id", state.buildId);
      const r_3 = await fetch(`${API}/api/runs/seed-finder/random?${params_0.toString()}`);
      const d = await r_3.json();
      if (d?.seed?.seed) router.push(`/seed-finder/${d.seed.seed}`);
    } catch {
      /* nothing to do */
    }
  }
  function applyHistory(qs_1: string) {
    seq.current += 1;
    inflight.current?.abort();
    setState(stateFromParams(new URLSearchParams(qs_1)));
    setResult(null);
    setProblem(null);
    setLimit(DEFAULT_LIMIT);
    autoRan.current = false;
  }
  const card = "rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4";
  const chip = "inline-flex items-center gap-1 rounded-md border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-2 py-1 text-xs text-[var(--text-primary)]";
  const tiny = "rounded border border-[var(--border-subtle)] bg-[var(--bg-card)] px-1 text-[10px] text-[var(--text-secondary)] hover:text-[var(--text-primary)]";
  const results = result?.results ?? [];
  const ready = hasPredicates(state);
  const canShowMore = result?.available === true && results.length >= limit && limit < MAX_LIMIT;
  const builds = indexInfo?.builds?.length ? indexInfo.builds : versions;
  const legacy = result?.engine === "legacy";
  function pickRow(key_3: ListKey, p_1: Pick, index_1: number, opts: {
    count?: boolean;
    act?: boolean;
    floor?: boolean;
  } = {}) {
    return <span key={`${formatPick(p_1)}:${index_1}`} className={chip}>
        <span>{nameOf(p_1.id)}</span>
        {opts.count && <button type="button" className={tiny} title={t("At least this many")} onClick={() => patchPick(key_3, index_1, {
        count: p_1.count >= MAX_COPIES ? 1 : p_1.count + 1
      })}>
            ×{p_1.count}
          </button>}
        {opts.act && <select aria-label={t("Act")} className={`${tiny} py-0.5`} value={p_1.act ?? ""} onChange={e_3 => patchPick(key_3, index_1, {
        act: e_3.target.value ? parseInt(e_3.target.value, 10) : null
      })}>
            <option value="">{t("any act")}</option>
            {[1, 2, 3, 4].map(a => <option key={a} value={a}>
                {t("act {n}", {
            n: a
          })}
              </option>)}
          </select>}
        {opts.floor && <input aria-label={t("By floor")} type="number" min={1} max={60} placeholder={t("by floor")} className={`${tiny} w-16 py-0.5`} value={p_1.floorMax ?? ""} onChange={e_4 => patchPick(key_3, index_1, {
        floorMax: e_4.target.value ? parseInt(e_4.target.value, 10) : null
      })} />}
        {(state.players ?? 1) > 1 && <select aria-label={t("Seat")} className={`${tiny} py-0.5`} value={p_1.seat ?? ""} onChange={e_5 => patchPick(key_3, index_1, {
        seat: e_5.target.value ? parseInt(e_5.target.value, 10) : null
      })}>
            <option value="">{t("any seat")}</option>
            {Array.from({
          length: state.players ?? 1
        }, (__0, i_3) => i_3 + 1).map(s_0 => <option key={s_0} value={s_0}>
                  {t("seat {n}", {
            n: s_0
          })}
                </option>)}
          </select>}
        <button type="button" aria-label={t("Remove")} className="ml-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]" onClick={() => dropPick(key_3, index_1)}>
          ×
        </button>
      </span>;
  }
  function section(key_4: ListKey, title: string, items: PickerItem[], placeholder: string, opts_0: {
    count?: boolean;
    act?: boolean;
    floor?: boolean;
  } = {}) {
    return <div>
        <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
          {title}
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {state[key_4].map((p_2, i_4) => pickRow(key_4, p_2, i_4, opts_0))}
          <div className="min-w-[14rem] flex-1">
            <EntityPicker placeholder={placeholder} items={items} disabled={items.length === 0} onPick={i_5 => addPick(key_4, i_5)} />
          </div>
        </div>
      </div>;
  }
  return <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center gap-3 mb-2">
        <h1 className="text-3xl font-bold">
          <span className="text-[var(--accent-gold)]">{t("Seed Finder")}</span>
        </h1>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border border-[var(--accent-gold)]/40 text-[var(--accent-gold)]">
          {t("Preview")}
        </span>
      </div>
      <p className="text-sm text-[var(--text-muted)] mb-2 max-w-3xl">
        {t("Search recorded runs and predicted seeds for Neow offers, card rewards, relics, events, ancients, bosses and shop stock. Predictions assume a fully unlocked profile and no modifiers.")}
      </p>
      <p className="text-sm text-[var(--text-secondary)] mb-2 max-w-3xl">
        {t("Seeds are locked to specific achievement unlocks. For the best experience, only use this tool when you're at max achievements and Ascension 10.")}
      </p>
      <p className="text-xs text-[var(--text-muted)] mb-6 max-w-3xl">
        {t("Main and beta hash seeds differently, so pick the version you play. Recorded matches appear before predictions. Predicted rewards and shops assume the displayed reward order.")}
        {indexInfo?.seeds ? ` ${t("{n} seeds indexed.", {
        n: indexInfo.seeds.toLocaleString()
      })}` : ""}
      </p>

      <div className={`${card} mb-4 grid gap-4`}>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)]">
            {t("Version")}
            <select className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-primary)] px-2 py-1 text-sm text-[var(--text-primary)]" value={state.buildId} onChange={e_6 => update({
            buildId: e_6.target.value
          })}>
              <option value="">{t("any version")}</option>
              {builds.map(v_0 => <option key={v_0} value={v_0}>
                  {v_0}
                </option>)}
            </select>
          </label>
          <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)]">
            {t("Lobby")}
            {[1, 2, 3, 4].map(n => <button key={n} type="button" onClick={() => update({
            players: n === 1 && state.players === null ? null : n,
            characters: state.characters.slice(0, n)
          })} className={`px-2.5 py-1 rounded-md border text-xs ${(state.players ?? 1) === n ? "bg-[var(--bg-card-hover)] border-[var(--border-accent)] text-[var(--text-primary)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-accent)]"}`}>
                {n === 1 ? t("Solo") : t("{n} players", {
              n
            })}
              </button>)}
          </div>
          <label className="flex items-center gap-2 text-xs text-[var(--text-secondary)] cursor-pointer">
            <input type="checkbox" checked={state.win} onChange={e_7 => update({
            win: e_7.target.checked
          })} className="accent-[var(--accent-gold)]" />
            {t("Won at least once")}
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-[var(--text-secondary)] mr-1">
            {(state.players ?? 1) > 1 ? t("Party") : t("Character")}
          </span>
          {SEED_FINDER_CHARACTERS.map(c_2 => {
          const on = state.characters.includes(c_2);
          return <button key={c_2} type="button" onClick={() => toggleCharacter(c_2)} className={`text-xs px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${on ? "bg-[var(--bg-card-hover)] border-[var(--border-accent)] text-[var(--text-primary)]" : "bg-[var(--bg-primary)] border-[var(--border-subtle)] text-[var(--text-secondary)] hover:border-[var(--border-accent)]"}`} style={on ? {
            borderColor: characterHex(c_2) || undefined
          } : undefined}>
                <CharacterTag id={c_2} name={t(characterLabel(c_2))} size={14} />
              </button>;
        })}
          {state.characters.length > 0 && <button type="button" className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)] ml-1" onClick={() => update({
          characters: []
        })}>
              {t("Any character")}
            </button>}
        </div>
      </div>

      {catalogError && <div className="mb-4">
          <LabUnavailable kind="catalog" onRetry={() => setCatalogTick(n_0 => n_0 + 1)} />
        </div>}

      <div className={`${card} mb-4 grid gap-5 md:grid-cols-2`}>
        {section("neow", t("Neow offers"), catalogs.ancientRelics, t("Add a Neow relic"))}
        {section("offered", t("Card rewards offered"), catalogs.cards, t("Add a card"), {
        count: true,
        act: true,
        floor: true
      })}
        {section("relics", t("Relics obtained"), catalogs.relics, t("Add a relic"), {
        floor: true
      })}
        {section("events", t("Events"), catalogs.events, t("Add an event"), {
        act: true
      })}
        {section("bosses", t("Bosses"), catalogs.bosses, t("Add a boss"), {
        act: true
      })}
        {section("elites", t("Elites"), catalogs.elites, t("Add an elite"), {
        act: true
      })}
        <div>
          <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
            {t("Ancient")}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {state.ancient && <span className={chip}>
                {nameOf(state.ancient)}
                <select aria-label={t("Act")} className={`${tiny} py-0.5`} value={state.ancientAct ?? ""} onChange={e_8 => update({
              ancientAct: e_8.target.value ? parseInt(e_8.target.value, 10) : null
            })}>
                  <option value="">{t("any act")}</option>
                  {[1, 2, 3, 4].map(a_0 => <option key={a_0} value={a_0}>
                      {t("act {n}", {
                  n: a_0
                })}
                    </option>)}
                </select>
                <button type="button" aria-label={t("Remove")} className="ml-0.5 text-[var(--text-muted)] hover:text-[var(--text-primary)]" onClick={() => update({
              ancient: null,
              ancientAct: null
            })}>
                  ×
                </button>
              </span>}
            <div className="min-w-[14rem] flex-1">
              <EntityPicker placeholder={t("Which ancient appears")} items={catalogs.ancients} disabled={catalogs.ancients.length === 0} onPick={i_6 => update({
              ancient: i_6.id
            })} />
            </div>
          </div>
        </div>
        {section("ancientOffers", t("Ancient offers"), catalogs.ancientRelics, t("Add an ancient relic"), {
        act: true
      })}
        {section("deck", t("Final deck"), catalogs.cards, t("Add a card"), {
        count: true
      })}
        {section("shop", t("Shop stock"), [...catalogs.cards, ...catalogs.relics.map(r_4 => ({
        ...r_4,
        id: `RELIC:${r_4.id}`
      })), ...catalogs.potions.map(p_3 => ({
        ...p_3,
        id: `POTION:${p_3.id}`
      }))], t("Add something a shop sold"))}
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <button type="button" disabled={!ready || loading} onClick={() => search(limit)} className="rounded-md bg-[var(--accent-gold)] px-4 py-2 text-sm font-semibold text-on-accent disabled:opacity-50">
          {loading ? t("Searching...") : t("Search")}
        </button>
        <span className="text-xs text-[var(--text-muted)]">
          {t("stop after")}
        </span>
        {LIMIT_STEPS.map(n_1 => <button key={n_1} type="button" onClick={() => {
        setLimit(n_1);
        if (ready) search(n_1);
      }} className={`px-2 py-1 rounded-md border text-xs ${limit === n_1 ? "bg-[var(--bg-card-hover)] border-[var(--border-accent)] text-[var(--text-primary)]" : "bg-[var(--bg-card)] border-[var(--border-subtle)] text-[var(--text-secondary)]"}`}>
            {n_1}
          </button>)}
        <button type="button" onClick={copyLink} disabled={!ready} className="rounded-md border border-[var(--border-subtle)] px-3 py-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)] disabled:opacity-50">
          {copied === "__link__" ? t("Link copied") : t("Copy link")}
        </button>
        <button type="button" onClick={randomSeed} className="rounded-md border border-[var(--border-subtle)] px-3 py-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
          {t("Random seed")}
        </button>
        {ready && <button type="button" onClick={() => update({
        ...Object.fromEntries(LIST_KEYS.map(k => [k, []])),
        ancient: null,
        ancientAct: null
      })} className="text-xs text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            {t("Clear filters")}
          </button>}
      </div>

      {history.length > 0 && !ready && <div className="mb-6">
          <div className="text-xs uppercase tracking-wider text-[var(--text-muted)] mb-1.5">
            {t("Recent searches")}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {history.map(qs_2 => {
          const s_1 = stateFromParams(new URLSearchParams(qs_2));
          const summary = LIST_KEYS.flatMap(k_0 => s_1[k_0].map(p_4 => nameOf(p_4.id))).concat(s_1.ancient ? [nameOf(s_1.ancient)] : []).slice(0, 4).join(", ");
          return <button key={qs_2} type="button" onClick={() => applyHistory(qs_2)} className={`${chip} hover:border-[var(--border-accent)]`}>
                  {summary || t("empty search")}
                  <span className="text-[var(--text-muted)]">
                    · {predicateCount(s_1)}
                  </span>
                </button>;
        })}
          </div>
        </div>}

      {problem && <LabUnavailable kind={problem} onRetry={() => search(limit)} />}

      {result?.available && <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[var(--text-muted)]">
            <span>
              {results.length === 1 ? t("{n} seed", {
            n: 1
          }) : t("{n} seeds", {
            n: results.length
          })}
            </span>
            {legacy && <span>
                {t("The seed index is still building on this box, so this is the older run scan.")}
              </span>}
            {results.length === 0 && <span>
                {t("No played seed matches all of that yet. Loosen a filter.")}
              </span>}
          </div>
          {results.map(r_5 => <div key={`${r_5.seed}:${r_5.build_id}:${r_5.party.join("+")}`} className={card}>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <button type="button" onClick={() => copy(r_5.seed, r_5.seed)} title={t("Copy seed")} className="font-mono text-lg font-semibold text-[var(--accent-gold)] hover:underline">
                  {r_5.seed}
                </button>
                <span className="text-xs text-[var(--text-muted)]">
                  {copied === r_5.seed ? t("Copied") : r_5.build_id}
                </span>
                {r_5.predicted && <span className="rounded border border-[var(--accent-teal)] px-2 py-0.5 text-xs text-[var(--accent-teal)]">
                    {t("Predicted")}
                  </span>}
                {r_5.party.map(c_3 => <CharacterTag key={c_3} id={c_3} name={t(characterLabel(c_3))} size={14} />)}
                {r_5.players > 1 && <span className="text-xs text-[var(--text-muted)]">
                    {t("{n} players", {
              n: r_5.players
            })}
                  </span>}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[var(--text-secondary)] mb-2">
                <span>
                  {r_5.predicted ? t("Fully unlocked, no modifiers") : t("{n} runs", {
              n: r_5.runs
            })}
                  {r_5.win_rate != null && <>
                      {" · "}
                      <span className={r_5.wins > 0 ? "text-success" : ""}>
                        {t("{p}% wins", {
                  p: r_5.win_rate
                })}
                      </span>
                    </>}
                </span>
                {r_5.best_run && <Link href={r_5.best_run.url} className="text-[var(--text-primary)] hover:underline">
                    {r_5.best_run.win ? t("fastest win {time}", {
              time: formatTime(r_5.best_run.run_time)
            }) : t("best run, floor {n}", {
              n: r_5.best_run.floors ?? "?"
            })}
                  </Link>}
                {r_5.replay && <Link href={r_5.replay.url} className="text-[var(--accent-teal)] hover:underline">
                    {t("watch replay")}
                  </Link>}
                <Link href={`/seed-finder/${r_5.seed}?${new URLSearchParams({
            ...(r_5.build_id ? {
              build_id: r_5.build_id
            } : {}),
            party: r_5.party.join(",")
          })}`} className="text-[var(--accent-gold)] hover:underline">
                  {t("inspect seed")}
                </Link>
              </div>
              {(r_5.neow_offers.length > 0 || r_5.bosses.length > 0) && <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-muted)] mb-2">
                  {r_5.neow_offers.length > 0 && <span>
                      {t("Neow")}: {r_5.neow_offers.map(nameOf).join(", ")}
                    </span>}
                  {r_5.bosses.length > 0 && <span>
                      {t("Bosses")}:{" "}
                      {r_5.bosses.map(b => `${b.act}. ${nameOf(b.id)}`).join(", ")}
                    </span>}
                </div>}
              <div className="flex flex-wrap gap-1.5">
                {r_5.matched.map(tag_0 => <span key={tag_0} className="inline-flex items-center gap-1 rounded-md border border-success/40 bg-success/10 px-2 py-0.5 text-xs text-success">
                    {labelFor(tag_0)}
                    {r_5.where[tag_0]?.length ? <span className="text-[var(--text-muted)]">
                        {r_5.where[tag_0].slice(0, 3).map(w => w.act && w.floor ? `${w.act}-${w.floor}` : w.floor ? `f${w.floor}` : "").filter(Boolean).join(" ")}
                      </span> : null}
                  </span>)}
              </div>
            </div>)}
          {canShowMore && <button type="button" onClick={() => {
        const next_2 = Math.min(MAX_LIMIT, limit + DEFAULT_LIMIT);
        setLimit(next_2);
        search(next_2);
      }} className="rounded-md border border-[var(--border-subtle)] px-3 py-2 text-xs text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
              {t("Show more")}
            </button>}
        </div>}
    </div>;
}